import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { ComputedRef } from "vue";
import { computed, ref } from "vue";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import type { HypotheticalFrequency, PredictiveCalcs } from ".";
import { injectStrict } from "@/utils/utils";
import {
  parametersKey,
  pumpsMapKey,
  wellPropertiesKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { getPredictedDesiredFlowRateSTBD } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import type { HypotheticalFlowDataSet } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";

/**
 * This function is used to generate the predictions about different operating frequencies
 * based on a single scenario.
 *
 * A simple loop through the set of frequencies does not work for predicitions because
 * the range in which the prediction algo makes guesses depends on the operating point.
 * Therefore, the predictions need to happen as two separate sequences
 * traversing the set in either direction from the design frequency.
 *
 * https://stackoverflow.com/questions/38462000/difference-between-object-assign-and-object-spread
 *
 * @param scenario Scenario to use for origin of predicitons
 * @param frequencies Array of pump frequencies to use for calculations
 * @returns map of ScenarioCalculations to frequency
 */
const usePredictiveCalcsInject = (
  destructuredScenario: DestructuredScenario,
  frequencies: HypotheticalFrequency[]
): ComputedRef<PredictiveCalcs> => {
  const wellProperties = injectStrict(wellPropertiesKey);
  const parameters = injectStrict(parametersKey).value;
  const pumps = injectStrict(pumpsMapKey);
  const operatingFrequency =
    destructuredScenario.parameters.value?.pump_hz.value || 60;

  // The order for the lower set of frequencies must be sorted high to low
  // because we traverse the frequencies starting at the original design frequency
  const lowerHypotheticalFrequenciesHz = frequencies
    .filter(f => f.frequency <= operatingFrequency)
    .sort((a, z) => z.frequency - a.frequency);

  // The opposite is true for the upper set of frequencies which must
  // be sorted from low to high
  const upperHypotheticalFrequenciesHz = frequencies
    .filter(f => f.frequency > operatingFrequency)
    .sort((a, z) => a.frequency - z.frequency);

  const getPredictedCalcs = (frequencies: number[]) => {
    return frequencies.reduce<PredictiveCalcs>((map, frequency) => {
      const hypotheticalParameters = ref<NonNullable<OperatingParameters>>({
        ...destructuredScenario.parameters.value,
        ...parameters,
        __typename: "scenario_parameters"
      });

      const hypotheticalCalcs = useScenarioCalculations({
        ...destructuredScenario,
        wellProperties,
        pumps,
        parameters: hypotheticalParameters
      });

      hypotheticalParameters.value.pump_hz = frequency;

      const predictedFlowRateDataSet = getPredictedDesiredFlowRateSTBD(
        hypotheticalCalcs,
        hypotheticalParameters.value
      );

      hypotheticalParameters.value.target_liquid_rate_std_barrel_day =
        predictedFlowRateDataSet.targetRow.hypotheticalFlowRateSTBD;

      return {
        ...map,
        [frequency]: {
          hypotheticalCalcs,
          predictedFlowRateDataSet
        }
      };
    }, {});
  };

  const lowerReportCalcs = getPredictedCalcs(
    lowerHypotheticalFrequenciesHz.map(hf => hf.frequency)
  );

  const upperReportCalcs = getPredictedCalcs(
    upperHypotheticalFrequenciesHz.map(hf => hf.frequency)
  );

  return computed(() => ({
    ...upperReportCalcs,
    ...lowerReportCalcs
  }));
};

// Calcs for one hypothetical frequency, at the flow rate predicted for it.
// Each frequency starts from the scenario's own parameters, so frequencies
// are independent of each other and can be predicted in parallel (see the
// tornado chart worker). A prediction made elsewhere can be passed in to
// skip the solve; it is the same deterministic result.
const buildPredictiveCalc = (
  destructuredScenario: DestructuredScenario,
  frequency: number,
  precomputedFlowRateDataSet?: HypotheticalFlowDataSet
) => {
  const hypotheticalParameters = ref<NonNullable<OperatingParameters>>({
    ...destructuredScenario.parameters.value,
    __typename: "scenario_parameters"
  });

  const hypotheticalCalcs = useScenarioCalculations({
    ...destructuredScenario,
    parameters: hypotheticalParameters
  });

  hypotheticalParameters.value.pump_hz = frequency;
  const predictedFlowRateDataSet =
    precomputedFlowRateDataSet ??
    getPredictedDesiredFlowRateSTBD(
      hypotheticalCalcs,
      hypotheticalParameters.value
    );

  hypotheticalParameters.value.target_liquid_rate_std_barrel_day =
    predictedFlowRateDataSet.targetRow.hypotheticalFlowRateSTBD;

  return { hypotheticalCalcs, predictedFlowRateDataSet };
};

// The flow rate prediction for one frequency, on its own. Used by the tornado
// chart worker to predict frequencies in parallel.
export const predictFlowRateDataSetAtFrequency = (
  destructuredScenario: DestructuredScenario,
  frequency: number
): HypotheticalFlowDataSet =>
  buildPredictiveCalc(destructuredScenario, frequency).predictedFlowRateDataSet;

export type PrecomputedPredictions = Record<number, HypotheticalFlowDataSet>;

const usePredictiveCalcsNoInject = (
  destructuredScenario: DestructuredScenario,
  frequencies: HypotheticalFrequency[],
  precomputedPredictions?: PrecomputedPredictions
): ComputedRef<PredictiveCalcs> => {
  const operatingFrequency =
    destructuredScenario.parameters.value?.pump_hz.value || 60;

  // The order for the lower set of frequencies must be sorted high to low
  // because we traverse the frequencies starting at the original design frequency
  const lowerHypotheticalFrequenciesHz = frequencies
    .filter(f => f.frequency <= operatingFrequency)
    .sort((a, z) => z.frequency - a.frequency);

  // The opposite is true for the upper set of frequencies which must
  // be sorted from low to high
  const upperHypotheticalFrequenciesHz = frequencies
    .filter(f => f.frequency > operatingFrequency)
    .sort((a, z) => a.frequency - z.frequency);

  const getPredictedCalcs = (frequencies: number[]) => {
    return frequencies.reduce<PredictiveCalcs>(
      (map, frequency) => ({
        ...map,
        [frequency]: buildPredictiveCalc(
          destructuredScenario,
          frequency,
          precomputedPredictions?.[frequency]
        )
      }),
      {}
    );
  };

  const lowerReportCalcs = getPredictedCalcs(
    lowerHypotheticalFrequenciesHz.map(hf => hf.frequency)
  );

  const upperReportCalcs = getPredictedCalcs(
    upperHypotheticalFrequenciesHz.map(hf => hf.frequency)
  );

  return computed(() => ({
    ...upperReportCalcs,
    ...lowerReportCalcs
  }));
};

export const usePredictiveCalcs = (
  destructuredScenario: DestructuredScenario,
  frequencies: HypotheticalFrequency[],
  useEvaluationOverrides?: boolean,
  precomputedPredictions?: PrecomputedPredictions
) => {
  if (useEvaluationOverrides) {
    return usePredictiveCalcsInject(destructuredScenario, frequencies);
  } else {
    return usePredictiveCalcsNoInject(
      destructuredScenario,
      frequencies,
      precomputedPredictions
    );
  }
};
